import type { Scene } from '@/types';

/**
 * Capítulo 1 — "Ceniza".
 *
 * Rework literario completo. Prosa rioplatense densa con registros
 * que oscilan entre Poe (sensoriedad obsesiva), King (interior cotidiano)
 * y Rolón (subtexto psicoanalítico). Nada de hedges tipo "si tenés X";
 * todas las variantes se resuelven por `[if]` o por ramas de escena.
 */
export const CAP1_ESCENAS: Scene[] = [
  // ===========================================================================
  //  ACTO I — "La casa que ardió en su propia boca"
  // ===========================================================================
  {
    id: 'cap1_intro',
    capitulo: 1,
    titulo: 'Ceniza',
    background: { type: 'fire', hint: 'cabaña consumiéndose en mitad de la noche' },
    musica: 'fire_chaos',
    texto: [
      'No es el calor del fogón. Esa es la primera idea que te llega, y llega antes que los ojos entiendan que están abiertos. El fogón lo apagaste antes de dormir, como todas las noches desde hace siete años, con la constancia avara del que ya no se permite el lujo de olvidarse de las cosas que matan. Este calor es otro. Este calor tiene la paciencia del animal que lleva rato esperándote.',
      'Abrís los ojos y la pared de tu izquierda ya no es pared. Es una lámina naranja que se retuerce como una lengua que aprendió el idioma equivocado. El techo cruje con una voz que no es de madera: es la voz que la madera hace cuando entiende, tarde, que va a dejar de ser madera.',
      'Pipo no está donde Pipo duerme. El cajón de paja está vacío y tibio. La puerta, que cerraste con pestillo ayer a las diez, cuelga abierta sobre sus goznes. [emph]Alguien la abrió[/emph]. Tenés esa certeza como tenés el gusto a humo en los dientes: instantánea, sin opción.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'incendio_visto', valor: true }],
    opciones: [
      {
        id: 'buscar_pipo',
        texto: 'Pegar un alarido con su nombre y tirarte al bosque antes de pensarlo.',
        destino: 'cap1_patio_ceniza',
        intencion: 'empatico',
        efectos: [
          { kind: 'stat', stat: 'vinculo', delta: 2 },
          { kind: 'flag', flagId: 'pipo_buscado_primero', valor: true },
          { kind: 'xp', cantidad: 20 },
        ],
      },
      {
        id: 'adentro_salvar',
        texto: 'Meter la mano en el fuego. Sacar lo que se pueda. Después correr.',
        destino: 'cap1_adentro_salvar',
        intencion: 'astuto',
        efectos: [
          { kind: 'stat', stat: 'astucia', delta: 1 },
          { kind: 'xp', cantidad: 10 },
        ],
      },
      {
        id: 'camino_lejos',
        texto: 'Salir por el camino y no mirar atrás. Los carpinchos se asustan y después vuelven.',
        destino: 'cap1_camino',
        intencion: 'neutral',
        efectos: [
          { kind: 'flag', flagId: 'pipo_abandonado_inicio', valor: true },
          { kind: 'scheduleConsequence', consequenceId: 'trigger:cap1_villa_entrada:abandono_inicial' },
          { kind: 'xp', cantidad: 5 },
        ],
      },
    ],
  },

  {
    id: 'cap1_adentro_salvar',
    capitulo: 1,
    background: { type: 'fire', hint: 'interior de la cabaña ardiendo por dentro' },
    texto: [
      'Te metés adentro como el que mete el brazo en agua muy fría: decidido a que duela. El humo te recibe con la cortesía del enemigo viejo; te conoce, sabe cuánto aguantás.',
      'La daga de tu padre está colgada donde siempre. Tiene un mango de asta cuarteado, la hoja manchada por una sangre que no es tuya y un nombre grabado en letra chiquita que no leíste nunca en voz alta. La descolgás y el acero está frío —frío, con la cabaña entera ardiendo—, y esa imposibilidad te distrae tres segundos de más.',
      'Una viga se parte a dos metros. El aire se vuelve polvo ardiente. Tenés tiempo para el morral y para una raíz de luz seca, no para más. La libreta se queda. La ropa se queda. [shake]La casa se queda.[/shake]',
      'Salís al patio con las cejas chamuscadas y una vergüenza nueva: la de haber elegido el metal antes que al bicho.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'salvó_daga_paterna', valor: true },
      { kind: 'stat', stat: 'vida', delta: -10 },
      { kind: 'item', itemId: 'daga_paterna', delta: 1 },
      { kind: 'item', itemId: 'morral', delta: 1 },
      { kind: 'item', itemId: 'raiz_luz', delta: 1 },
    ],
    opciones: [
      {
        id: 'salir_patio',
        texto: 'Salir al patio. Gritar al bosque el nombre que ya no se acuerda de vos.',
        destino: 'cap1_patio_ceniza',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_patio_ceniza',
    capitulo: 1,
    background: { type: 'ruins', hint: 'patio de tierra con cabaña ardiendo atrás, amanecer gris' },
    texto: [
      'El patio huele a resina quemada y a una cosa dulce que no querés nombrar. Atrás, la cabaña se dobla sobre sí misma, un animal que decidió al fin morirse con dignidad. Adelante, el bosque. Adentro del bosque, posiblemente, tu único amigo.',
      'Gritás [pipo]"¡Pipo!"[/pipo] y la voz sale rota, ajena. El eco vuelve pelado. Un carpincho de cuatro años no corre así de lejos por susto: corre así de lejos cuando algo más grande lo empuja.',
      'En la tierra húmeda del patio hay huellas. Las de Pipo —anchas, pesadas—, y al lado otras. [if skill=ojo_cuervo]Con ojo entrenado las ubicás al instante: bota de caña baja, suela con muescas profundas, paso largo de varón alto que camina apurado pero sin correr.[/if][if noskill=ojo_cuervo]Hay otras, sí. No sabrías decir de quién. Más grandes que las de Pipo, más chicas que las tuyas. Alguien que pesa poco. Alguien apurado.[/if]',
    ],
    onEnter: [{ kind: 'flag', flagId: 'vio_huellas_patio', valor: true }],
    opciones: [
      {
        id: 'seguir_huellas',
        texto: 'Seguir las dos series de huellas. No es pregunta.',
        destino: 'cap1_bosque_humo',
        intencion: 'valiente',
        efectos: [{ kind: 'xp', cantidad: 10 }],
      },
      {
        id: 'inspeccionar_ruinas',
        texto: 'Antes de ir al bosque: chequear las ruinas. Algo que se salvó del fuego te sirve en el camino.',
        destino: 'cap1_inspeccionar_ruinas',
        intencion: 'astuto',
        condiciones: [{ kind: 'stat', stat: 'astucia', minimo: 2 }],
        alFallarCondicion: 'atenuar',
      },
    ],
  },

  {
    id: 'cap1_inspeccionar_ruinas',
    capitulo: 1,
    background: { type: 'ruins', hint: 'escombros humeantes, luz oblicua del alba' },
    texto: [
      'Te agachás entre las tablas retorcidas. El calor que se desprende todavía te corre por los antebrazos. Encontrás dos cosas que el fuego, por descuido o por aburrimiento, no terminó de comerse.',
      'Una piedrita redonda, pulida, del tamaño de un huevo de codorniz. Tiene un grabado en espiral que no reconocés pero que te acuerda vagamente de las monedas viejas que el Viejo Urso te mostraba cuando eras chico y te llamaba "pichón". La guardás sin saber por qué.',
      'Y un frasco con una tintura roja que te regaló una vez una mujer de paso que pasó por acá y no quiso decirte su nombre. Te dijo que lo guardaras para cuando algo te mordiera. Cuando te lo dio vos te reíste.',
    ],
    onEnter: [
      { kind: 'item', itemId: 'moneda_ancestral', delta: 1 },
      { kind: 'item', itemId: 'tintura_roja', delta: 1 },
      { kind: 'stat', stat: 'astucia', delta: 1 },
      { kind: 'xp', cantidad: 20 },
    ],
    opciones: [
      {
        id: 'ir_al_bosque',
        texto: 'Guardar todo y entrar al bosque. El alba no va a esperar.',
        destino: 'cap1_bosque_humo',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_camino',
    capitulo: 1,
    background: { type: 'ruins', hint: 'camino de tierra al amanecer, humo lejano' },
    texto: [
      'Camínas sin apurarte. Es el paso del que se convence a sí mismo: una frase vieja, repetida en voz baja, hasta que el cuerpo le cree. [emph]Pipo vuelve. Pipo siempre vuelve.[/emph]',
      'El camino está solo, como todas las mañanas de tu vida acá. La diferencia es que hoy, sin querer, girás la cabeza cada veinte pasos. Cada vez que la girás, la casa se ve más chica. Cada vez que se ve más chica, algo adentro tuyo se parte de un modo distinto.',
      'A la tercera curva entendés algo que no querías entender. Los carpinchos que se asustan no escapan hacia el corazón del bosque. Los carpinchos que se asustan vuelven al olor del que los cría. Pipo no volvió. Eso no es un susto. Eso es otra cosa.',
    ],
    opciones: [
      {
        id: 'desandar_volver',
        texto: 'Frenar. Darte vuelta. Desandar los veinte pasos y los veinte y los veinte hasta el bosque.',
        destino: 'cap1_bosque_humo',
        intencion: 'empatico',
        efectos: [
          { kind: 'stat', stat: 'empatia', delta: 2 },
          { kind: 'flag', flagId: 'pipo_abandonado_inicio', valor: false },
          { kind: 'xp', cantidad: 15 },
        ],
      },
      {
        id: 'seguir_al_pueblo',
        texto: 'Seguir al pueblo. Alguien ahí sabe algo. Alguien siempre sabe algo.',
        destino: 'cap1_bosque_humo',
        intencion: 'neutral',
        efectos: [{ kind: 'flag', flagId: 'insistió_en_irse', valor: true }],
      },
    ],
  },

  // ===========================================================================
  //  ACTO II — "Un carpincho habla el nombre que te pusieron"
  // ===========================================================================
  {
    id: 'cap1_bosque_humo',
    capitulo: 1,
    background: { type: 'forest', hint: 'bosque con humo entre los troncos, amanecer gris' },
    texto: [
      'El bosque es otro a esta hora. El bosque que conocés de día es amable con vos, te deja pasar, te tolera como se tolera a un huésped viejo que ya no molesta. El bosque del amanecer con humo no te conoce. Tiene el gesto del que está por pedirte que te vayas.',
      'Cada tanto el humo se arremolina como si respirara. No es metáfora: arriba de vos, entre las copas, hay una corriente de aire que vos no sentís pero que las hojas sí, y las hojas transpiran ese humo de un modo que hace pensar en pulmones. [shake]Pulmones grandes, dormidos a medias.[/shake]',
      'Avanzás treinta, cuarenta, cien pasos. El suelo está blando. Entre dos troncos hay algo que no se mueve del todo pero tampoco está quieto del todo. Una silueta en forma de animal o de hombre, imposible saber, porque la luz que entra es la luz de ningún lado.',
    ],
    opciones: [
      {
        id: 'avanzar_silueta',
        texto: 'Avanzar hacia la silueta sin esconderte. Que te vea.',
        destino: 'cap1_encuentro_urro',
        intencion: 'valiente',
        efectos: [{ kind: 'stat', stat: 'valentia', delta: 1 }],
      },
      {
        id: 'rodear_silueta',
        texto: 'Rodearla. No por miedo: por costumbre vieja.',
        destino: 'cap1_bosque_rodeo',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
      {
        id: 'gritar_silueta',
        texto: '[emph]"¿Quién anda ahí?"[/emph] La voz sale más firme que lo que sentís.',
        destino: 'cap1_encuentro_urro',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap1_bosque_rodeo',
    capitulo: 1,
    background: { type: 'forest', hint: 'sendero escondido entre raíces, luz filtrada' },
    texto: [
      'Rodeás la silueta con un arco largo, como te enseñó el Viejo Urso a rodear a los perros desconocidos: de espaldas al sol, el sol al enemigo. Pero el sol todavía no salió. Y la silueta, cuando te das vuelta, ya no está.',
      'Lo que sí está es un sendero. Un sendero que no figura en ningún mapa tuyo, aunque vos trazaste todos los mapas de este bosque de memoria. Las pisadas son chicas, casi circulares, demasiado parejas. Alguien las hizo a propósito, para que se siguieran.',
      'Alguien quiere que camines por acá. La única pregunta decente es [glow color=mystic]quién[/glow].',
    ],
    onEnter: [{ kind: 'flag', flagId: 'siguió_sendero_secreto', valor: true }],
    opciones: [
      {
        id: 'seguir_sendero',
        texto: 'Seguir. La curiosidad te arrastra más que el miedo.',
        destino: 'cap1_encuentro_urro',
        intencion: 'astuto',
        efectos: [{ kind: 'xp', cantidad: 10 }],
      },
    ],
  },

  {
    id: 'cap1_encuentro_urro',
    capitulo: 1,
    titulo: 'Urro',
    background: { type: 'mystical', hint: 'claro en el bosque, primera luz verdadera del día' },
    musica: 'mystical_loop',
    texto: [
      'La silueta se corporiza cuando estás a diez pasos. Y es un carpincho. Y no lo es.',
      'Es más grande que Pipo, más oscuro. Está parado en dos patas con una soltura que resulta ofensiva a todo lo que creíste saber sobre ellos. Tiene al cuello un collar hecho con semillas de ceibo ensartadas con tiento, y los ojos —ojos de carpincho, redondos, paciences— te miran como si te hubiera mirado antes, muchas veces, desde lejos. Como si vos fueras el animal conocido.',
      'Abre la boca y [glow color=mystic]dice tu nombre[/glow]. No el apodo que usaba el Viejo Urso, no el que gritabas al agua cuando eras chico. Tu nombre entero, el que pusieron tus padres. Lo dice mal, con la lengua rasposa del que aprendió humano tarde, pero lo dice.',
      '—Franco —repite—. Llegaste. Te tardaste menos de lo que pensaba.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'primer_contacto_capibara', valor: true },
      { kind: 'flag', flagId: 'conocido_clan_rio', valor: true },
      { kind: 'unlockLocation', locacionId: 'bosque_susurrante' },
      { kind: 'xp', cantidad: 35 },
    ],
    opciones: [
      {
        id: 'urro_donde_pipo',
        texto: 'Lo único que te importa, lo único que te sale: [emph]"¿Dónde está Pipo?"[/emph]',
        destino: 'cap1_urro_responde',
        intencion: 'empatico',
      },
      {
        id: 'urro_vos_quien_sos',
        texto: '[emph]"Antes decime quién sos vos y cómo sabés mi nombre."[/emph] Manos cerradas. No hace falta la daga para temblar.',
        destino: 'cap1_urro_responde',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
      {
        id: 'urro_silencio',
        texto: 'Callar. Dejar que hable. El que habla primero pierde, decía tu padre.',
        destino: 'cap1_urro_responde',
        intencion: 'neutral',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap1_urro_responde',
    capitulo: 1,
    background: { type: 'mystical', hint: 'claro en el bosque al amanecer' },
    texto: [
      'Urro —así dice que se llama, mientras mastica una hoja que estaba esperando ser masticada— te contesta con una calma que no es paciencia sino otra cosa. Es la calma del que está haciendo un trámite viejo: el de abrirle los ojos a un humano, tarea fatigosa pero conocida.',
      '—Pipo está vivo. No lo tengo yo. Lo tienen, y no son amigos. Tampoco son lo que vos pensás que son, porque vos todavía no pensás nada claro sobre esto. No te culpo: acabás de perder la casa. La casa se pierde una sola vez en la vida, y cuando pasa, a uno le sale de adentro una pregunta que no es la pregunta que hay que hacer.',
      '—La pregunta que hay que hacer —sigue— es por qué te quemaron la casa a vos y no a cualquier otro. La respuesta va a llevar el resto de esta aventura.',
      '[if flag=pipo_abandonado_inicio][emph]—Y una cosa más, Franco. Te vi no volver.[/emph] La voz de Urro se afila. [emph]—Cuando Pipo huyó al bosque, vos agarraste para el otro lado. Eso también va a tener respuesta. Tenelo presente.[/emph][/if]',
    ],
    opciones: [
      {
        id: 'seguir_a_urro',
        texto: 'Seguirlo. Lo que te espera del otro lado del bosque no puede ser peor que lo que dejaste.',
        destino: 'cap1_camino_villa',
        intencion: 'valiente',
        efectos: [
          { kind: 'unlockLocation', locacionId: 'villa_capibarica' },
          { kind: 'xp', cantidad: 30 },
        ],
      },
      {
        id: 'urro_exigir',
        texto: '[emph]"Decime ahora quién prendió el fuego. No voy a caminar a ciegas."[/emph]',
        destino: 'cap1_urro_pide_confianza',
        intencion: 'hostil',
      },
    ],
  },

  {
    id: 'cap1_urro_pide_confianza',
    capitulo: 1,
    background: { type: 'mystical', hint: 'claro, Urro mirando de costado' },
    texto: [
      'Urro hace algo que no esperabas: se ríe. Una risa de carpincho es más bien un chillido corto, pero lo que hay detrás es risa. Risa cansada, la de los viejos que ya vieron tres veces el mismo berrinche.',
      '—Si te lo digo ahora, no me creés. Y si no me creés, no vas a hacer lo que hay que hacer. La información, Franco, es como el agua: a ciertas temperaturas te salva, a otras te ahoga.',
      '—Vos venís conmigo y lo averiguás. O te quedás acá, te lamés las heridas y te morís antes del invierno de una infección ridícula. Las dos opciones son válidas. Solo una incluye a Pipo.',
    ],
    opciones: [
      {
        id: 'caminar_callado',
        texto: 'Caminar. Callado.',
        destino: 'cap1_camino_villa',
        intencion: 'neutral',
        efectos: [
          { kind: 'unlockLocation', locacionId: 'villa_capibarica' },
          { kind: 'xp', cantidad: 25 },
        ],
      },
    ],
  },

  {
    id: 'cap1_camino_villa',
    capitulo: 1,
    titulo: 'Lo que el bosque te decía sin decir',
    background: { type: 'forest', hint: 'sendero escondido, mediamañana, luz verde' },
    texto: [
      'Caminan callados un rato largo. Urro va adelante con ese trote suyo que no es elegante pero sí eficiente, como el de las mujeres del mercado cuando ya saben qué van a comprar. Cada tanto te hace una seña con el hocico, una que aprendés a leer: [pipo]no pises ahí[/pipo], [pipo]acá pasó algo hace dos días[/pipo], [pipo]huele raro[/pipo].',
      'Vos creés que conocés este bosque. Lo cartografiaste de memoria años atrás, cada arroyo, cada árbol caído. Pero Urro te está mostrando otro bosque: uno que se superpone al tuyo como un mantel sobre una mesa. Lo que era para vos "el árbol del ahorcado" para él es un sitio de memoria; lo que era "el sendero del cerdito salvaje" para él es una frontera. El mapa de Urro tiene otra escala.',
      'A media caminata, en un claro donde el sol pega por primera vez, se para. Se da vuelta. Te mira.',
      '—Antes de entrar a mi casa —dice— escuchame una cosa. Los míos te van a querer por lo que simbolizás. Algunos no te van a querer por lo mismo. No te encariñes con ninguno de los dos. La gente que te quiere por lo que simbolizás es la gente que después te va a odiar por lo que resultás ser.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'camino_villa_recorrido', valor: true }],
    opciones: [
      {
        id: 'preguntar_urro_historia',
        texto: '[emph]"¿Cuántos humanos como yo vinieron antes?"[/emph]',
        destino: 'cap1_urro_historia_humanos',
        intencion: 'astuto',
      },
      {
        id: 'preguntar_urro_pipo',
        texto: '[emph]"¿Vos sabías que Pipo existía antes de que yo lo rescatara?"[/emph]',
        destino: 'cap1_urro_historia_pipo',
        intencion: 'empatico',
      },
      {
        id: 'preguntar_urro_simbolo',
        texto: '[emph]"¿Qué carajo simbolizo yo?"[/emph]',
        destino: 'cap1_urro_simbolo',
        intencion: 'hostil',
      },
    ],
  },

  {
    id: 'cap1_urro_historia_humanos',
    capitulo: 1,
    background: { type: 'forest', hint: 'claro con un tronco grande atravesando' },
    texto: [
      '—Cuatro antes que vos —dice Urro—. Los cuatro vivieron solos en el bosque mucho tiempo. A los cuatro los elegimos por tener una cualidad específica: no querer compañía humana. Eso no es huraña. Eso es una herida que no cicatrizó. Cuatro heridos como vos.',
      '—El primero se suicidó antes de enterarse de nosotros. El segundo nos traicionó a la segunda semana, por dinero. Al tercero lo cuidamos tres años y después se volvió loco, empezó a hablar con piedras. Al cuarto —y acá Urro pausa más de lo debido— le sacamos lo que teníamos que sacarle y lo devolvimos al mundo humano con la memoria limpia. No sabe que existimos. No nos quiere ver nunca más. Ese fue el caso más exitoso.',
      '—Vos sos el quinto. No te asustes. Vos sos el quinto hace rato. Lo que pasa es que recién ahora te enterás.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'sabe_de_otros_humanos', valor: true }],
    opciones: [
      {
        id: 'seguir_despues_historia',
        texto: 'Tragar saliva. Seguir caminando.',
        destino: 'cap1_villa_primera_vista',
        intencion: 'neutral',
        efectos: [{ kind: 'xp', cantidad: 15 }],
      },
    ],
  },

  {
    id: 'cap1_urro_historia_pipo',
    capitulo: 1,
    background: { type: 'forest', hint: 'claro oblicuo, sol entre ramas' },
    texto: [
      'Urro se sienta sobre las patas traseras. Un gesto que hasta esta mañana hubieras leído como "descanso animal". Ahora lo leés como ceremonia: el gesto del que se prepara para decir algo importante.',
      '—Pipo nació en cautiverio, Franco. No huérfano: [emph]cautiverio[/emph]. Pibe, no es lo mismo. La camada de su madre —tres crías, las otras dos no sobrevivieron— nació en un pozo de los nuestros. Lo robaron cuando todavía tenía los ojos cerrados. Lo tiraron a un arroyo, envuelto en un trapo, para que lo encontrara un humano casualmente. Vos.',
      '—Nunca fue casualidad, Franco. Te lo pusieron en el camino. Y confiamos en que lo ibas a criar bien. Por eso, durante cuatro años, dejamos que lo quisieras sin interrumpir. Ahora, tarde, te enterás de que el cariño que le diste fue real pero no fue gratuito.',
      'Vos abrís la boca para contestar y no te sale nada. Urro asiente como si eso fuera lo correcto.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sabe_pipo_plantado', valor: true },
      { kind: 'xp', cantidad: 25 },
    ],
    opciones: [
      {
        id: 'seguir_despues_pipo',
        texto: 'Seguir caminando, con una bronca nueva contra algo que no sabés identificar.',
        destino: 'cap1_villa_primera_vista',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_urro_simbolo',
    capitulo: 1,
    background: { type: 'forest', hint: 'claro con flor blanca' },
    texto: [
      'Urro ladea la cabeza. La pregunta le gustó. Lo nota el modo en que demora la respuesta.',
      '—Para algunos, sos el humano que no los mata. Para otros, sos la prueba de que convivir con ustedes es posible. Para otros, sos la prueba de que convivir con ustedes es inevitable. Para otros —y estos son los que te van a dar problemas— sos el síntoma. El síntoma de una enfermedad que ellos llaman [glow color=mystic]contaminación[/glow], y que nosotros llamamos simplemente presente.',
      '—Cada vez que un carpincho de los míos te ve, elige —sin saber que elige— una de esas cuatro lecturas. Vos no sabés cuál te tocó en cada caso. Y yo no te lo voy a decir a tiempo para que te sirva. No porque no quiera. Porque no sirve que lo sepas.',
    ],
    onEnter: [{ kind: 'xp', cantidad: 20 }],
    opciones: [
      {
        id: 'seguir_despues_simbolo',
        texto: 'Asentir como quien recibe una profecía mal traducida.',
        destino: 'cap1_villa_primera_vista',
        intencion: 'astuto',
      },
    ],
  },

  // ===========================================================================
  //  ACTO III — "La villa que siempre estuvo escuchando"
  // ===========================================================================
  {
    id: 'cap1_villa_primera_vista',
    capitulo: 1,
    titulo: 'Villa Capibárica',
    background: { type: 'village', hint: 'aldea oculta entre arboles, chozas de caña junto al río' },
    musica: 'village_warm',
    texto: [
      'La villa no se ve hasta que estás adentro. Es un truco de la geografía, pero también de la voluntad: los senderos que la rodean están diseñados para que el ojo acostumbrado al cuadrado y al camino recto no los registre. Uno pasa por el borde sin verlo mil veces antes de que alguien, del otro lado, le abra una puerta invisible.',
      'Cuando Urro te hace cruzar el último arbusto, te encontrás con veinte, treinta chozas de caña trenzada sobre pasarelas de madera, armadas sobre un brazo del río que vos conocías como "brazo muerto". [emph]No estaba muerto[/emph]. Simplemente estaba ocupado por otros.',
      'Los fogones humean a la altura justa para que el humo se mezcle con la niebla de la mañana y desaparezca. Por eso nunca los viste desde lejos. Por eso durante siete años viviste a cinco kilómetros de una civilización completa y creíste que estabas solo.',
      'Cuando das tu primer paso sobre la pasarela, se hace un silencio. No el silencio del miedo: el silencio del [shake]reconocimiento[/shake]. Veintitantos carpinchos, en dos patas o en cuatro, te miran como se mira a un familiar que se fue a la guerra y no se sabía si volvía.',
    ],
    onEnter: [
      { kind: 'unlockLocation', locacionId: 'rio_ancestral' },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'saludar_abierto',
        texto: 'Levantar la mano. Saludar sin palabras. Que vean que no venís con violencia.',
        destino: 'cap1_villa_nayade',
        intencion: 'empatico',
        efectos: [{ kind: 'reputation', facciónId: 'clan_rio', delta: 10 }],
      },
      {
        id: 'caminar_atras',
        texto: 'Caminar dos pasos atrás de Urro. Dejar que él sea el que avanza. Observar.',
        destino: 'cap1_villa_nayade',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap1_villa_nayade',
    capitulo: 1,
    titulo: 'Náyade',
    background: { type: 'village', hint: 'interior de choza central, fogón bajo, tapiz en pared' },
    texto: [
      'Te llevan a la choza central. Adentro, sentada en una silla de caña tejida con una paciencia sin apuro, está una carpincha vieja. Tiene el pelaje casi blanco en los costados del hocico. Los ojos le brillan de un modo que vos solo viste en tus abuelas antes de morir: la mezcla exacta de lucidez terminal y cansancio acumulado.',
      'Se llama Náyade. No te lo dice ella: lo dice Urro, desde atrás tuyo, con la voz bajita del que acompaña a un invitado a meterse en la casa de un dios menor pero no por eso menos dios.',
      '—Sentate, Franco —dice Náyade. Te señala un banco frente a ella. Su voz tiene una musicalidad vieja, como de canción que le cantaban a uno antes del lenguaje—. Alguien te va a traer sopa. No preguntes de qué está hecha. No te va a gustar la respuesta, y no importa: te va a hacer bien.',
      'Te traen la sopa. Tiene razón. No preguntás.',
    ],
    onEnter: [
      { kind: 'stat', stat: 'vida', delta: 15 },
      { kind: 'stat', stat: 'mana', delta: 5 },
    ],
    opciones: [
      {
        id: 'nayade_que_soy',
        texto: '[emph]"¿Qué soy para ustedes?"[/emph]',
        destino: 'cap1_nayade_revelacion',
        intencion: 'empatico',
      },
      {
        id: 'nayade_que_es_pipo',
        texto: '[emph]"¿Qué es Pipo?"[/emph] Vas derecho. Para rodeos tenés el resto de la vida.',
        destino: 'cap1_nayade_revelacion',
        intencion: 'valiente',
      },
      {
        id: 'nayade_dame_acciones',
        texto: '[emph]"Salteemos la parte ceremonial. Decime qué tengo que hacer para traerlo de vuelta."[/emph]',
        destino: 'cap1_nayade_revelacion',
        intencion: 'hostil',
      },
    ],
  },

  {
    id: 'cap1_nayade_revelacion',
    capitulo: 1,
    titulo: 'El hijo que no era hijo',
    background: { type: 'village', hint: 'interior choza, luz de fogón' },
    texto: [
      'Náyade cierra los ojos un segundo. Cuando los abre, tiene otro peso en la mirada. El peso de decir en voz alta algo que prefería tener guardado.',
      '—Pipo es hijo de Tusqui. Tusqui fue nuestro último Gran Guardián. El Guardián no es un rey, Franco: es un equilibrio. Cuando un Guardián vive, las facciones del bosque no se cortan entre sí. Cuando muere, se cortan. Tusqui murió hace cinco años, envenenado por La Sombra. Su hembra estaba preñada. Sacamos a las crías vivas antes de que se nos llevaran el cuerpo.',
      '—De esas crías, dos murieron en la primera luna. La que sobrevivió fue Pipo. Lo escondimos. Lo hicimos pasar por carpincho común. Y cuando tuvo edad de dejar la teta, calculamos la fecha y te lo dejamos en el arroyo. No fue casualidad, Franco. Nunca fue casualidad. Fuimos nosotros.',
      '—Lo cuidaste cuatro años como si fuera tuyo. Era nuestro. Y al mismo tiempo, mientras lo cuidabas, era el tuyo también. Las dos cosas son ciertas. La que te duele no anula a la otra.',
      '[if flag=pipo_abandonado_inicio]Náyade te mira con una dureza que no estabas esperando. [emph]—Y lo abandonaste anoche. Eso también es cierto. Lo discutimos después, pero no lo olvido.[/emph][/if][if noflag=pipo_abandonado_inicio]Náyade hace una pausa. [emph]—Y anoche lo buscaste antes de buscarte. Eso no lo olvido. Era lo que esperábamos de vos, pero aún así no es poca cosa.[/emph][/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sabe_de_pipo_guardian', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'nayade_callar_asumir',
        texto: 'Tragar. Bajar la cabeza. Asentir como quien recibe un diagnóstico viejo que siempre sospechó.',
        destino: 'cap1_ibra_llega',
        intencion: 'empatico',
        efectos: [{ kind: 'stat', stat: 'empatia', delta: 1 }],
      },
      {
        id: 'nayade_bronca',
        texto: '[emph]"Me usaron como niñera."[/emph] No es pregunta. Es una cuchilla corta.',
        destino: 'cap1_ibra_llega',
        intencion: 'hostil',
        efectos: [{ kind: 'reputation', facciónId: 'clan_rio', delta: -5 }],
      },
      {
        id: 'nayade_tecnico',
        texto: '[emph]"¿Quiénes se lo llevaron, dónde lo tienen, cuánto tiempo tengo?"[/emph] Tres preguntas, en ese orden.',
        destino: 'cap1_ibra_llega',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap1_ibra_llega',
    capitulo: 1,
    titulo: 'Ibra',
    background: { type: 'village', hint: 'plaza central de la villa, ruedas de mercader' },
    texto: [
      'Un chillido afuera te sacude. No es un chillido de alarma: es un chillido teatral, de los que usan los pregoneros en los pueblos humanos cuando quieren que te acerques a regañadientes.',
      '—Abran paso a Ibra, abran paso a Ibra —dice una voz que modula la frase como quien afina un instrumento barato—. Un Ibra cansado cansa a tres Ibras descansados, dicen por ahí. Abran paso.',
      'Náyade suelta una sonrisa chica, la de quien va a dejar pasar a un hermano querido y problemático. —Llegó el mercader —dice—. Bueno, llegó el único que tenemos. Le conviene que sea único: si hubiera competencia, lo fundían antes del invierno. Andá. Decile a Ibra que te atienda. Y no compres caro, Franco: a los ermitaños nos ven cara de fáciles.',
      'Salís al sol flojo de la plaza central. Ibra es un carpincho de panza generosa, ojos chiquitos y rápidos, con un chaleco hecho de lona humana que le vendieron en otro puesto. Dos bolsos a los lados. Cara de haber visto demasiado y de haber hablado aún más.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ibra_conocido', valor: true },
    ],
    opciones: [
      {
        id: 'ibra_saludar',
        texto: 'Acercarte. Presentarte.',
        destino: 'cap1_ibra_trueque',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_ibra_trueque',
    capitulo: 1,
    titulo: 'Ibra, trueques',
    background: { type: 'village', hint: 'Ibra sentado entre sus bolsos' },
    texto: [
      'Ibra te mira de arriba a abajo como si calculara precio al aire que respirás.',
      '—Vos sos el ermitaño. Te reconozco por el olor a humo reciente, es distinto al del carpincho fumado que vende Urro a veces. [emph]Qué cara de hambre tenés, querido[/emph]. Trueco lo que quieras. No doy crédito, no hago promesas y no discuto precios dos veces.',
      'Te muestra sus existencias. Todo parece útil y todo parece justo suficientemente caro.',
    ],
    opciones: [
      {
        id: 'ibra_comprar_elixir',
        texto: 'Cambiarle la daga de tu padre por un frasco de elixir de vida.',
        destino: 'cap1_ibra_trueque',
        intencion: 'astuto',
        condiciones: [{ kind: 'item', itemId: 'daga_paterna' }],
        alFallarCondicion: 'ocultar',
        efectos: [
          { kind: 'item', itemId: 'daga_paterna', delta: -1 },
          { kind: 'item', itemId: 'elixir_vida', delta: 1 },
          { kind: 'stat', stat: 'vidaMax', delta: 10 },
          { kind: 'stat', stat: 'vida', delta: 10 },
        ],
      },
      {
        id: 'ibra_comprar_amuleto',
        texto: 'Cambiarle la moneda ancestral por un amuleto de vínculo.',
        destino: 'cap1_ibra_trueque',
        intencion: 'empatico',
        condiciones: [{ kind: 'item', itemId: 'moneda_ancestral' }],
        alFallarCondicion: 'ocultar',
        efectos: [
          { kind: 'item', itemId: 'moneda_ancestral', delta: -1 },
          { kind: 'item', itemId: 'amuleto_vinculo', delta: 1 },
          { kind: 'stat', stat: 'vinculo', delta: 2 },
        ],
      },
      {
        id: 'ibra_comprar_vela',
        texto: 'Cambiarle la tintura roja por una vela ritual.',
        destino: 'cap1_ibra_trueque',
        intencion: 'neutral',
        condiciones: [{ kind: 'item', itemId: 'tintura_roja' }],
        alFallarCondicion: 'ocultar',
        efectos: [
          { kind: 'item', itemId: 'tintura_roja', delta: -1 },
          { kind: 'item', itemId: 'vela_ritual', delta: 1 },
        ],
      },
      {
        id: 'ibra_info_plaga',
        texto: 'Pagarle con palabras: preguntarle qué se dice en los caminos.',
        destino: 'cap1_ibra_info',
        intencion: 'astuto',
      },
      {
        id: 'ibra_cerrar',
        texto: 'Agradecerle y volver a Náyade. Hay misión pendiente.',
        destino: 'cap1_consejo_mision',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_ibra_info',
    capitulo: 1,
    background: { type: 'village', hint: 'Ibra acercando la cabeza' },
    texto: [
      'Ibra se acerca el hocico a la oreja tuya. Huele a pescado seco y a resina vieja. Habla más bajo.',
      '—Por el sur, tres aldeas capibaras se quemaron en los últimos dos meses. No por rayo, Franco. Los incendios los prenden [emph]cazadores humanos[/emph] pagados por alguien de tu pueblo. Alguien con mucha plata y muchas ganas de estas tierras.',
      '—La Sombra dice que pelea contra eso, pero los veo moviéndose en los mismos senderos que los cazadores. No estoy diciendo que estén juntos. Estoy diciendo que no se tocan, y en un bosque tan chico, no tocarse es casi coordinarse. Pensalo.',
      '—Eso te lo doy gratis. El resto te cuesta.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ibra_reveló_cazadores_pagos', valor: true },
      { kind: 'xp', cantidad: 20 },
    ],
    opciones: [
      {
        id: 'ibra_volver_trueque',
        texto: 'Volver al mostrador. Ver qué más tiene.',
        destino: 'cap1_ibra_trueque',
        intencion: 'neutral',
      },
      {
        id: 'ibra_despedir_final',
        texto: 'Despedirte. Volver con Náyade.',
        destino: 'cap1_consejo_mision',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_consejo_mision',
    capitulo: 1,
    titulo: 'La misión',
    background: { type: 'village', hint: 'choza central, consejo reunido' },
    texto: [
      'Cuando volvés a lo de Náyade, hay tres carpinchos más sentados en círculo. Uno viejo, musculoso, con una cicatriz en diagonal atravesando el cuello —Orón, de los Guardianes del Fuego, te dicen, aunque no te saluda—. Una hembra joven y ansiosa —Sena, que será tu compañera—. Y Urro, detrás tuyo, que ya se comió media vida en vaivenes como este.',
      '—Un cazador humano viene dando vueltas por el sector oeste desde hace doce días —dice Náyade—. Ayer dejó una trampa nueva, mal escondida. O es novato —y no creemos que lo sea— o nos está dejando saber que está ahí. Queremos saber tres cosas: quién es, para quién trabaja, y si trae información sobre el Santuario. El Santuario es donde tenemos razones para creer que tienen a Pipo.',
      '—No te vamos a mandar solo. Vas con Urro y Sena. Orón viene si se aburre. Es muy probable que se aburra, porque Orón se aburre fácil.',
      'Orón hace un ruido con la garganta que podría ser aprobación o desprecio.',
    ],
    onEnter: [
      { kind: 'item', itemId: 'raiz_luz', delta: 2 },
      { kind: 'xp', cantidad: 20 },
    ],
    opciones: [
      {
        id: 'aceptar_mision',
        texto: '[emph]"Vamos ahora."[/emph]',
        destino: 'cap1_patrulla_inicio',
        intencion: 'valiente',
        efectos: [
          { kind: 'reputation', facciónId: 'clan_rio', delta: 10 },
          { kind: 'xp', cantidad: 20 },
        ],
      },
      {
        id: 'pedir_noche',
        texto: '[emph]"Dame la noche. Necesito dormir."[/emph]',
        destino: 'cap1_noche_villa',
        intencion: 'astuto',
      },
      {
        id: 'preguntar_oron',
        texto: 'Clavarle a Orón: [emph]"¿Por qué vos no lo hiciste ya?"[/emph]',
        destino: 'cap1_oron_responde',
        intencion: 'hostil',
      },
    ],
  },

  {
    id: 'cap1_oron_responde',
    capitulo: 1,
    background: { type: 'village', hint: 'Orón mira desde su esquina' },
    texto: [
      'Orón gira la cabeza despacio. Tiene los ojos de un color que no es un color: es una ausencia de color, como una moneda lavada por mucha agua.',
      '—Porque si voy yo, lo mato —dice—. Y Náyade quiere información. Yo no vine a traer información. Yo vine a traer cuerpos. A vos te mandan a vos porque vos todavía dudás. Ese es tu valor, Franco. No es tuyo, pero es valor. Aprovechalo mientras lo tenés.',
      'Sena baja la mirada. Urro suspira. Náyade no dice nada. Vos entendés algo que no te gusta: no te están mandando porque confíen. Te están mandando porque todavía [emph]no sos nadie[/emph], y eso es útil.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'oron_se_presentó_hostil', valor: true },
    ],
    opciones: [
      {
        id: 'oron_aceptar_igual',
        texto: 'Aceptar igual. El valor de uno no se define por cómo lo usan los demás.',
        destino: 'cap1_patrulla_inicio',
        intencion: 'valiente',
        efectos: [
          { kind: 'stat', stat: 'valentia', delta: 1 },
          { kind: 'xp', cantidad: 20 },
        ],
      },
    ],
  },

  {
    id: 'cap1_noche_villa',
    capitulo: 1,
    background: { type: 'village', hint: 'villa de noche, una hamaca colgada bajo pasarela' },
    texto: [
      'Te dan una hamaca tejida entre dos postes. El río corre abajo con ese sonido que siempre tuvieron los ríos de verdad: el sonido de una conversación que vos no estás siguiendo pero que no para.',
      'No dormís bien. Nadie duerme bien la primera noche después de que se le quemó la casa. Pero dormís. A las tres de la mañana sentís un peso chico sobre las piernas y te despertás de golpe. Es una cría de capibara que no conocés. Te mira un segundo. Se baja. Se va corriendo. No sabés si fue sueño.',
      'Al amanecer tenés claro lo único que tenía que quedarte claro: [glow color=moss]no vas a dejar a Pipo ahí[/glow].',
    ],
    onEnter: [
      { kind: 'stat', stat: 'vida', delta: 15 },
      { kind: 'stat', stat: 'resistencia', delta: 1 },
    ],
    opciones: [
      {
        id: 'arrancar_patrulla',
        texto: 'Buscar a Urro. Arrancar.',
        destino: 'cap1_patrulla_inicio',
        intencion: 'valiente',
        efectos: [{ kind: 'xp', cantidad: 20 }],
      },
    ],
  },

  // ===========================================================================
  //  ACTO IV — "El cazador tiene un nombre y una culpa"
  // ===========================================================================
  {
    id: 'cap1_patrulla_inicio',
    capitulo: 1,
    titulo: 'La patrulla',
    background: { type: 'forest', hint: 'bosque medio, luz de mediodía filtrada' },
    texto: [
      'El bosque en patrulla es otro bosque. Urro adelante, Sena un paso atrás tuyo, vos en el medio como si fueras frágil. Todos avanzan en silencio. El silencio de tres no es el silencio de uno: es un silencio que escucha, que espera, que a veces se rompe con una mirada, no con una palabra.',
      'El sector oeste tiene árboles más flacos y el suelo más duro. Se ve menos vida pero se oye más: grillos de día, un pájaro nervioso que repite la misma nota cuatro veces y se calla, como si le faltara una quinta y no la encontrara.',
      'Cerca del mediodía, Sena para de golpe. Apunta con el hocico a veinte metros adelante. Hay un tipo agachado al lado de un árbol, instalando con paciencia infinita una trampa metálica del tamaño de una cama. Es un humano flaco, de espalda, con sombrero de ala ancha.',
    ],
    opciones: [
      {
        id: 'sigilo_acercarse',
        texto: 'Sigilo. Bordear. Plantarse del otro lado y cerrarle las salidas.',
        destino: 'cap1_interceptar',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
      {
        id: 'frente_directo',
        texto: 'Hacia él. Directo. Que te vea venir.',
        destino: 'cap1_combate_cazador',
        intencion: 'valiente',
      },
      {
        id: 'observar_primero',
        texto: 'No acercarse aún. Observarlo trabajar un rato. Entender cómo piensa.',
        destino: 'cap1_observar_cazador',
        intencion: 'astuto',
        condiciones: [{ kind: 'stat', stat: 'astucia', minimo: 3 }],
        alFallarCondicion: 'atenuar',
      },
    ],
  },

  {
    id: 'cap1_observar_cazador',
    capitulo: 1,
    background: { type: 'forest', hint: 'visión oculta, cazador trabajando metódicamente' },
    texto: [
      'Te quedás treinta pasos atrás, detrás de un tronco, con Urro pegado al costado. Sena desaparece —una habilidad suya: es tan chica que cabe en los intervalos del bosque—.',
      'El cazador trabaja con una prolijidad que te llama la atención. Cada cinco minutos saca una libreta, anota, guarda. Acomoda la trampa con tres movimientos exactos. Cuando se agacha, tiene la misma curva en la espalda que tenía tu padre cuando limpiaba su rifle: la curva del hombre que sabe que todo lo que hace en este instante puede salir mal mañana y por eso lo hace hoy con devoción.',
      'No es novato. Y no está cazando animales: está haciendo un [emph]relevamiento[/emph]. Las trampas son secundarias. Lo principal es esa libreta.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sabe_cazador_releva', valor: true },
      { kind: 'stat', stat: 'astucia', delta: 1 },
    ],
    opciones: [
      {
        id: 'acercarse_saber',
        texto: 'Ya está. Acercarse. Con lo que sabés, la conversación empieza distinto.',
        destino: 'cap1_interceptar',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap1_interceptar',
    capitulo: 1,
    background: { type: 'forest', hint: 'cazador de espaldas, acorralado' },
    texto: [
      'Se dan vuelta y ya la tenés rodeada. Urro de un lado, Sena del otro, vos enfrente. El cazador se queda quieto con una mano en el aire. La otra, despacio, deja caer algo al suelo: un cuchillo corto, de punta.',
      'Es una mujer. Cuarenta, cuarenta y dos. Piel del sur, callos en las manos de años de trabajo viejo. Los ojos le brillan con una rabia que es más vieja que vos.',
      '—Me llamo Mara —dice, antes de que nadie le pregunte—. No vine a matar carpinchos. No me importa si me creen. Pero necesito que no me maten ustedes tampoco hasta que termine la frase siguiente.',
      'Urro inclina la cabeza. Sena asiente apenas. Vos no decís nada.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'mara_interceptada', valor: true }],
    opciones: [
      {
        id: 'dejar_hablar',
        texto: '[emph]"Terminá la frase."[/emph]',
        destino: 'cap1_mara_hablar',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
      {
        id: 'atacar_igual',
        texto: 'No. La frase siguiente es tuya: daga al aire. Por la casa que te quemaron.',
        destino: 'cap1_combate_cazador',
        intencion: 'hostil',
      },
      {
        id: 'empatia_mara',
        texto: '[emph]"Bajá las manos y sentate. Vas a hablar con más comodidad."[/emph]',
        destino: 'cap1_mara_hablar',
        intencion: 'empatico',
        condiciones: [{ kind: 'stat', stat: 'empatia', minimo: 3 }],
        alFallarCondicion: 'atenuar',
      },
    ],
  },

  {
    id: 'cap1_combate_cazador',
    capitulo: 1,
    titulo: 'Mara',
    background: { type: 'forest', hint: 'pelea en claro del bosque' },
    texto: [
      'Mara se endereza, abandona la libreta, junta el cuchillo del suelo. La rabia que tiene adentro la conoce; la usó otras veces. Urro gruñe una advertencia. Sena busca cubierta.',
    ],
    combate: {
      enemyId: 'cazador_humano',
      onVictoria: 'cap1_mara_vencida',
      onDerrota: 'cap1_mara_te_venció',
      onHuida: 'cap1_mara_te_venció',
    },
    opciones: [],
  },

  {
    id: 'cap1_mara_vencida',
    capitulo: 1,
    background: { type: 'forest', hint: 'Mara arrodillada, sangrando de una ceja' },
    texto: [
      'Le ganás. No fácil. Te deja la ceja partida y un corte feo en el antebrazo, pero cae y no se levanta rápido. Le arrancás la libreta. Urro te mira con algo que podría ser aprobación o sorpresa de que no la rematás.',
      'Mara se sienta con las manos en la cabeza. —Esperaba esto —dice—. Una de dos: ustedes, o los míos. Los míos hubieran sido peor.',
      'La libreta tiene nombres, fechas, rutas de villas capibaras. La última anotación dice: [glow color=ember]"entregar al Cuervo en Santuario, luna nueva, sin fallas"[/glow].',
    ],
    onEnter: [
      { kind: 'item', itemId: 'libreta_cazador', delta: 1 },
      { kind: 'flag', flagId: 'tiene_libreta_cazador', valor: true },
      { kind: 'unlockLocation', locacionId: 'santuario' },
    ],
    opciones: [
      {
        id: 'dejar_ir_mara',
        texto: 'Dejarla ir. Con la libreta en tu poder, ella ya no sirve de escudo.',
        destino: 'cap1_retorno_villa',
        intencion: 'empatico',
        efectos: [
          { kind: 'stat', stat: 'empatia', delta: 1 },
          { kind: 'flag', flagId: 'mara_liberada', valor: true },
        ],
      },
      {
        id: 'llevar_mara_villa',
        texto: 'Llevarla atada a la Villa. Que Náyade decida qué hacer con ella.',
        destino: 'cap1_retorno_villa',
        intencion: 'astuto',
        efectos: [
          { kind: 'flag', flagId: 'mara_prisionera', valor: true },
          { kind: 'reputation', facciónId: 'guardianes_fuego', delta: 10 },
        ],
      },
    ],
  },

  {
    id: 'cap1_mara_te_venció',
    capitulo: 1,
    background: { type: 'forest', hint: 'Franco en el piso, Urro parado enfrente' },
    texto: [
      'Te derrumbás. El suelo tiene un olor específico que vos no conocías: olor a raíz podrida y a caca de pájaro. Lo registras con el mismo desapego con que registrás el cielo partiendose entre las copas.',
      'No te matan. Mara pelea con Urro y Sena, y los tres terminan en un empate provisorio donde ninguno quiere seguir. Mara escapa. Te ponen vendas y te cargan de vuelta. La libreta se perdió en el alboroto.',
    ],
    onEnter: [
      { kind: 'stat', stat: 'vida', delta: 20 },
      { kind: 'flag', flagId: 'derrotado_por_mara', valor: true },
      { kind: 'scheduleConsequence', consequenceId: 'trigger:cap1_plan_santuario:falla_patrulla' },
    ],
    opciones: [
      {
        id: 'retorno_humillado',
        texto: 'Volver a la Villa con la cabeza baja.',
        destino: 'cap1_retorno_villa',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_mara_hablar',
    capitulo: 1,
    background: { type: 'forest', hint: 'Mara apoyada contra un tronco, más serena' },
    texto: [
      'Mara se sienta sin que nadie se lo pida dos veces. Deja el cuchillo a dos metros, gesto costoso. Se pasa la mano por la cara, se la mira como si estuviera descubriendo que tiene manos.',
      '—Trabajo para un tipo que se hace llamar el Cuervo. Paga bien, no hace preguntas, no aparece nunca en persona. Nos pide relevamientos: cuántos carpinchos, dónde duermen, qué rutas usan. No nos pide cazar. Cazan otros. A mí me tocó esta zona porque conozco el bosque y porque, según él, tengo "la suficiente bronca con los carpinchos". La bronca la tengo por algo que me pasó hace años, no viene al caso. La suficiente nunca la tuve.',
      '—En el Santuario, en luna nueva —eso es en tres días—, el Cuervo recibe a sus reportes. Voy con la libreta, le cuento lo que vi, me paga. Nunca vi qué hace con la información. Te diría que lo adiviné, pero mentiría. No quise adivinarlo.',
      '—Pueden matarme o pueden usarme. Soy buena en lo mío. Si me usan, entran al Santuario con una cara que el Cuervo ya conoce. Si me matan, pierden eso. Elijan ustedes.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'mara_cooperante', valor: true },
      { kind: 'unlockLocation', locacionId: 'santuario' },
      { kind: 'item', itemId: 'libreta_cazador', delta: 1 },
    ],
    opciones: [
      {
        id: 'mara_liberar',
        texto: '[emph]"Dame la libreta y andate. Que te pierdas del mapa."[/emph]',
        destino: 'cap1_retorno_villa',
        intencion: 'empatico',
        efectos: [
          { kind: 'stat', stat: 'empatia', delta: 1 },
          { kind: 'flag', flagId: 'mara_liberada', valor: true },
          { kind: 'reputation', facciónId: 'clan_rio', delta: 5 },
        ],
      },
      {
        id: 'mara_usar',
        texto: '[emph]"Venís con nosotros. Nos metés al Santuario."[/emph]',
        destino: 'cap1_retorno_villa',
        intencion: 'astuto',
        efectos: [
          { kind: 'flag', flagId: 'mara_aliada', valor: true },
          { kind: 'stat', stat: 'astucia', delta: 1 },
          { kind: 'xp', cantidad: 30 },
        ],
      },
      {
        id: 'mara_llevar_atada',
        texto: '[emph]"Venís con las manos atadas y la lengua libre. Hablás en la Villa."[/emph]',
        destino: 'cap1_retorno_villa',
        intencion: 'neutral',
        efectos: [
          { kind: 'flag', flagId: 'mara_prisionera', valor: true },
          { kind: 'reputation', facciónId: 'guardianes_fuego', delta: 8 },
        ],
      },
    ],
  },

  // ===========================================================================
  //  ACTO V — "El santuario no es lo que creen"
  // ===========================================================================
  {
    id: 'cap1_retorno_villa',
    capitulo: 1,
    titulo: 'El informe',
    background: { type: 'village', hint: 'consejo reunido, libreta sobre mesa' },
    texto: [
      'Náyade lee la libreta despacio. Orón lee por encima del hombro, con la impaciencia de un perro viejo que huele la carne. Sena, que entró la última, se queda cerca de la puerta: no se acostumbra a cerrarse dentro de una choza cuando puede estar afuera, escuchando al bosque.',
      '—Luna nueva, tres días —dice Náyade, al fin—. Es la primera ventana clara en meses. Si el Cuervo está recibiendo reportes en persona en el Santuario, es porque algo grande está por pasar. Los Cuervos no se exponen por nada.',
      '[if flag=mara_aliada]—Y tenés una puerta abierta, Franco. Mara los puede meter sin que los adviertan. Eso vale más que diez combatientes.[/if][if flag=mara_liberada]—Y perdiste a la única puerta que teníamos. No lo digo como reproche: lo digo porque quiero que entiendas el costo de la compasión de hoy. Hay que entrar sin ella.[/if][if flag=mara_prisionera]—Y tenemos a la mensajera encerrada. Eso es una carta buena y una carta peligrosa. Si el Cuervo se entera de que no llegó, cambia el plan. Tenemos horas, no días.[/if][if flag=derrotado_por_mara]—Perdimos a Mara. Perdimos la libreta. No perdimos el dato: luna nueva, Santuario. Eso te lo guardó Sena de memoria. Gracias, Sena.[/if]',
    ],
    opciones: [
      {
        id: 'sumarse_plan',
        texto: '[emph]"Voy con ustedes."[/emph]',
        destino: 'cap1_plan_santuario',
        intencion: 'valiente',
        efectos: [
          { kind: 'reputation', facciónId: 'clan_rio', delta: 10 },
          { kind: 'xp', cantidad: 30 },
        ],
      },
      {
        id: 'pedir_prepararse',
        texto: '[emph]"Tres días. Me preparo."[/emph]',
        destino: 'cap1_preparacion_final',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap1_preparacion_final',
    capitulo: 1,
    background: { type: 'village', hint: 'villa al atardecer, tres personas alrededor del fogón' },
    texto: [
      'Tres días en una villa capibárica pueden ser larguísimos o cortísimos. Los tuyos son de los cortos. Aprendés a leer las corrientes del río mirando a Sena enseñarte. Compartís mate —sí, mate, Ibra consiguió de algún lado hace años— con Urro, y Urro no habla y eso también es una forma de enseñarte.',
      'Al segundo día, una cría te ofrece una piedra redonda con una carita pintada. No te dice nada. Te la pone en la mano y se va corriendo. Vos la guardás en el bolsillo. A veces las piedras con caritas pintadas son lo único que te salva de la noche.',
      '[if item=amuleto_vinculo]El amuleto que le trocaste a Ibra te late en el pecho las dos noches. No es palpitación: es otra cosa, un tirón cálido hacia algo que no está. Pipo, probablemente. Pipo, seguro.[/if]',
    ],
    onEnter: [
      { kind: 'stat', stat: 'vida', delta: 10 },
      { kind: 'stat', stat: 'mana', delta: 5 },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'listo_para_santuario',
        texto: 'Listo. Luna nueva hoy.',
        destino: 'cap1_plan_santuario',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap1_plan_santuario',
    capitulo: 1,
    titulo: 'Luna nueva',
    background: { type: 'mystical', hint: 'camino nocturno hacia el santuario' },
    musica: 'mystical_loop',
    texto: [
      'Salen al anochecer. La luna nueva no es "sin luna": es una luna apenas insinuada, un cuerno fino contra un cielo demasiado estrellado. Los bosques a la noche tienen una acústica distinta, como si el sonido buscara caminos nuevos ante la falta de luz para guiarse.',
      'Sena va de punta. Urro atrás tuyo. [if flag=mara_aliada]Mara camina entre los dos, con las manos libres pero la mirada perdida, como quien cumple un trámite al que ya se resignó.[/if][if flag=mara_prisionera]Mara va adelante de Urro, atada al cuello con una soga floja, con la boca tapada con un trapo. No porque fuera a gritar: para que nadie la reconozca si los ven de lejos.[/if][if flag=mara_liberada]No vas con Mara. La dejaste ir. Ahora el Santuario tiene ojos que vos no conocés, y hay que entrar a ciegas.[/if][if flag=derrotado_por_mara]Sin Mara. Sin libreta. Con lo que Sena retuvo de memoria. Con menos de lo que uno cree que necesita. Como casi todas las cosas importantes.[/if]',
      'A media noche aparece el Santuario. No es lo que esperabas.',
    ],
    opciones: [
      {
        id: 'ver_santuario',
        texto: 'Mirarlo entero antes de decidir.',
        destino: 'cap1_exterior_santuario',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_exterior_santuario',
    capitulo: 1,
    background: { type: 'mystical', hint: 'santuario derrumbado con velas prendidas en el interior' },
    texto: [
      'El Santuario no es decadencia pintoresca. No es ruina muerta. Es ruina viva: alguien mantiene velas prendidas en los huecos de las columnas, alguien barrió las hojas del piso con una regularidad terca, alguien armó nichos nuevos con piedras viejas. [glow color=ember]Es un templo en uso[/glow].',
      'Por las ventanas sin vidrio se ven sombras moviéndose. Se cuenta mal, pero son cinco o seis. Una voz grave conduce el intercambio. No la entendés del todo, pero el tono es el de alguien que no discute: que [emph]enseña[/emph].',
      '[if flag=mara_aliada]Mara te mira. —Esa es la voz. Es él. No sé cómo se llama. Solo se deja oír en estos ritos.[/if]',
    ],
    opciones: [
      {
        id: 'entrada_lateral',
        texto: 'Entrada lateral. Sena dice que hay una escalinata derrumbada por detrás.',
        destino: 'cap1_entrada_lateral',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
      {
        id: 'entrada_frontal',
        texto: 'Frontal. Con la daga y la cara alta.',
        destino: 'cap1_combate_guardia',
        intencion: 'valiente',
        condiciones: [{ kind: 'item', itemId: 'daga_paterna' }],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'usar_mara_entrada',
        texto: 'Mara entra primero. Que la "reciban". Ustedes atrás.',
        destino: 'cap1_mara_entra_primera',
        intencion: 'astuto',
        condiciones: [{ kind: 'flag', flagId: 'mara_aliada' }],
        alFallarCondicion: 'ocultar',
      },
    ],
  },

  {
    id: 'cap1_entrada_lateral',
    capitulo: 1,
    background: { type: 'cave', hint: 'escalinata rota, guardia haciendo ronda' },
    texto: [
      'La escalinata lateral es más baja que el piso del templo y huele a hojas fermentadas. Un guardia —carpincho grande, dos patas, armado con un diente fijado a una rama como cuchillo— hace rondas cada dos minutos exactos.',
      'Urro cuenta los segundos con el hocico. Te mira. —O lo esperás pasar y entrás detrás, o lo limpiamos ahora. El problema del primero es que si nos vio Sena lo habrían visto a él también: no estamos seguros de si ya nos registraron.',
    ],
    opciones: [
      {
        id: 'esperar_paso',
        texto: 'Esperar. Entrar cuando pase. Silencio total.',
        destino: 'cap1_interior_santuario',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
      {
        id: 'limpiar_guardia',
        texto: 'Limpiarlo. Ahora.',
        destino: 'cap1_combate_guardia',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap1_mara_entra_primera',
    capitulo: 1,
    background: { type: 'cave', hint: 'Mara caminando hacia el templo, sola' },
    texto: [
      'Mara camina sola hacia la entrada. No mira atrás. Vos te preguntás si es traición al Cuervo o traición a vos; ella probablemente no lo tiene claro tampoco.',
      'Entra. Saludan adentro. Le dicen algo que vos no oís. Ella contesta con voz firme. Cinco minutos eternos. Después sale al umbral, mira al cielo como quien calcula la hora, y hace con la mano el gesto mínimo que vos y Urro esperaban: [emph]pase libre, segundos contados[/emph].',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'mara_cubrió_entrada', valor: true },
      { kind: 'xp', cantidad: 40 },
    ],
    opciones: [
      {
        id: 'entrar_aprovechando',
        texto: 'Entrar ya. Sena primero, vos después, Urro atrás.',
        destino: 'cap1_interior_santuario',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap1_combate_guardia',
    capitulo: 1,
    titulo: 'Capibara de la Sombra',
    background: { type: 'cave', hint: 'escalinata, guardia levantando el arma' },
    texto: [
      'El guardia los ve antes de lo previsto. Un bramido agudo —alarma en clave, probablemente— y dos dientes al aire.',
    ],
    combate: {
      enemyId: 'capibara_sombra',
      onVictoria: 'cap1_interior_santuario',
      onDerrota: 'cap1_retirada_fracaso',
      onHuida: 'cap1_retirada_fracaso',
    },
    opciones: [],
  },

  {
    id: 'cap1_interior_santuario',
    capitulo: 1,
    titulo: 'El Cuervo',
    background: { type: 'cave', hint: 'interior del santuario, mesa de piedra, jaula de madera al fondo' },
    texto: [
      'Adentro, cinco carpinchos de pelaje oscuro se agrupan alrededor de una mesa de piedra. Al fondo, en una jaula de madera reforzada con hierro —hierro en manos de carpinchos, todavía te cuesta asumirlo—, hay un bulto chico. Se mueve. Levanta la cabeza.',
      'Es Pipo. Flaco, sucio, con un corte mal cicatrizado en el lomo. Cuando te ve, no chilla. Hace algo peor: te mira con una paciencia que parece vieja. La paciencia del que dejó de esperar hace unas horas y se está acostumbrando al abandono.',
      'El que está parado al lado de la mesa es grande. Más grande que Orón. Tiene tres cicatrices diagonales en el hocico y los ojos de un color que no existe. Cuando habla, la voz no tiene matices: parece leída.',
      '—Llegaste antes de lo que calculé, Franco. Eso es un error mío, no tuyo. Siéntense. Los cinco. Hay sopa, si quieren.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'encuentro_el_cuervo', valor: true },
    ],
    opciones: [
      {
        id: 'cuervo_escuchar',
        texto: 'No sentarse. Escuchar de pie.',
        destino: 'cap1_cuervo_habla',
        intencion: 'astuto',
      },
      {
        id: 'cuervo_ignorar',
        texto: 'Ignorarlo. Ir directo a la jaula.',
        destino: 'cap1_liberar_pipo',
        intencion: 'empatico',
        efectos: [{ kind: 'reputation', facciónId: 'sombra', delta: -10 }],
      },
      {
        id: 'cuervo_atacar',
        texto: 'Atacar ya. La conversación la dan los vivos.',
        destino: 'cap1_liberar_pipo',
        intencion: 'hostil',
        efectos: [
          { kind: 'stat', stat: 'valentia', delta: 1 },
          { kind: 'reputation', facciónId: 'sombra', delta: -20 },
        ],
      },
      {
        id: 'cuervo_spell',
        texto: 'Probar Susurro del Río en él. Calmarlo. Hacerle bajar la guardia.',
        destino: 'cap1_cuervo_hablauno_calmante',
        intencion: 'astuto',
        condiciones: [{ kind: 'spell', hechizoId: 'susurro_rio' }],
        alFallarCondicion: 'ocultar',
      },
    ],
  },

  {
    id: 'cap1_cuervo_habla',
    capitulo: 1,
    background: { type: 'cave', hint: 'Cuervo en silueta contra braseros' },
    texto: [
      'El Cuervo no se mueve. Habla despacio, como si cada palabra pesara lo que pesa.',
      '—La civilización que vos creés estar rescatando se va a extinguir. No por mí. Por la enfermedad que trajeron los tuyos, por la caza, por incendios que ni yo puedo parar. Náyade te va a pedir que elijas entre dignidad y supervivencia. Yo vine a decirte que hay otra puerta. Pero hoy no es el día de esa puerta. Hoy es el día en que te llevás al pibe.',
      '—No te voy a pelear. Te lo dejo. Cuando dudes —y vas a dudar, Franco, porque sos de los que dudan—, volvé a buscarme. Yo voy a estar. Es lo único que tengo, a esta altura: estar.',
      'Los otros cuatro capibaras no se mueven. Vos tampoco. Sena se desliza hacia la jaula.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cuervo_ofreció_puerta', valor: true },
      { kind: 'xp', cantidad: 40 },
    ],
    opciones: [
      {
        id: 'liberar_pipo_tras_hablar',
        texto: 'Ir por Pipo. Escuchaste suficiente.',
        destino: 'cap1_liberar_pipo',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap1_cuervo_hablauno_calmante',
    capitulo: 1,
    background: { type: 'cave', hint: 'Cuervo con los ojos entrecerrados, Franco con una mano extendida' },
    texto: [
      'Susurro del Río no calma a los calmos. Funciona en los tensos. El Cuervo es el ser más tenso en esta sala, y por un instante su postura se afloja un milímetro, apenas, y vos leés en ese milímetro lo que él nunca te iba a decir.',
      'Le duele. Le duele hace años. La Sombra no empezó como ideología: empezó como duelo. Tusqui, el Guardián que envenenaron, era hermano de él. No hermano de sangre —ellos usan "hermano" con otra lógica—, pero hermano igual. El Cuervo no persigue una doctrina. Persigue la posibilidad de justificarse.',
      'El hechizo se disuelve rápido. El Cuervo parpadea. Entiende que le leíste algo. No te lo reprocha. Solo inclina la cabeza un grado.',
      '—Bien jugado —dice—. Te espero la próxima.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'leyó_dolor_cuervo', valor: true },
      { kind: 'xp', cantidad: 60 },
    ],
    opciones: [
      {
        id: 'ir_por_pipo_tras_spell',
        texto: 'Ir a la jaula. Pipo te está esperando.',
        destino: 'cap1_liberar_pipo',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap1_liberar_pipo',
    capitulo: 1,
    titulo: 'Pipo',
    background: { type: 'cave', hint: 'jaula de madera, Pipo adentro' },
    texto: [
      'Rompés la jaula. La madera se abre con un crujido que parece agradecimiento. Pipo no se mueve al principio. Te mira fijo, como si estuviera calibrando. Después, muy despacio, camina los tres pasos que los separan y te apoya la cabeza contra la rodilla.',
      'No es un reencuentro de película. No hay música. No hay perdón verbal. Es un carpincho flaco que descansa el peso de su cabeza contra la rodilla de un humano que pensó que lo había perdido. Eso alcanza.',
      'Lo levantás en brazos. Huele a humedad, a miedo viejo, a una cosa dulce que ahora sabés que es el perfume que usan los carpinchos de la Sombra para marcar a sus trofeos. Le costará días sacárselo.',
      'Cuando salen, el Cuervo sigue parado donde estaba. Los mira irse. No dice nada más. La luna nueva los acompaña de vuelta.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'pipo_rescatado', valor: true },
      { kind: 'stat', stat: 'vinculo', delta: 5 },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'volver_a_villa',
        texto: 'Volver a la Villa. Despacio.',
        destino: 'cap1_fin_cap1',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap1_retirada_fracaso',
    capitulo: 1,
    background: { type: 'cave', hint: 'retirada con heridos' },
    texto: [
      'No entran. La retirada es apurada. Sena está herida. Urro maldice con palabras que no entendés. Vos tenés la cara partida en el lado que nunca te habían partido.',
      'Pipo sigue adentro. El Cuervo sigue vivo. La luna nueva se cerró y con ella la ventana. Tendrán que volver, pero la próxima vez va a ser peor.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'rescate_cap1_fracasado', valor: true },
      { kind: 'scheduleConsequence', consequenceId: 'trigger:cap2_intro:rescate_fallido' },
    ],
    opciones: [
      {
        id: 'volver_derrotado',
        texto: 'Volver sin él. Aprender a dormir sin él.',
        destino: 'cap1_fin_cap1',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  ACTO VI — "La primera luz gris"
  // ===========================================================================
  {
    id: 'cap1_fin_cap1',
    capitulo: 1,
    titulo: 'La primera luz gris',
    background: { type: 'village', hint: 'villa al amanecer, luz gris y frío' },
    musica: 'village_warm',
    texto: [
      'La villa los recibe con una quietud que no es silencio: es el silencio que tiene una casa adentro cuando alguien acaba de morir y todavía nadie lo dijo en voz alta. Solamente que acá nadie murió. O nadie que no supiéramos que iba a morir.',
      '[if flag=pipo_rescatado]Pipo duerme en tus brazos como si nunca hubiera estado en otro lado. Cuando entrás a la choza, Náyade te saca la manta que te colgaste al hombro y te la reacomoda como si fueras un chico. No te pregunta nada. Sabe leer las caras.[/if][if flag=rescate_cap1_fracasado]Pipo no vino. La cama tejida que te tenían preparada queda al costado como un accesorio ridículo. Náyade te pone una mano en el hombro. No te pregunta nada. Sabe leer las caras.[/if]',
      '—Dormí lo que puedas —dice, sin énfasis—. Mañana arranca otro capítulo. Los capítulos no siempre son mejores que los anteriores. A veces son peor, pero más cortos. Eso dicen los míos.',
      '[whisper](Fin del Capítulo 1 — "Ceniza". Seguimos en el Capítulo 2.)[/whisper]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap1_completado', valor: true },
      { kind: 'xp', cantidad: 100 },
    ],
    opciones: [
      {
        id: 'entrar_cap2',
        texto: 'Dejar que el sueño te gane. Entrar al Capítulo 2.',
        destino: 'cap2_intro',
        intencion: 'neutral',
      },
      {
        id: 'volver_menu_cap1',
        texto: 'Volver al inicio y probar otra rama.',
        destino: 'cap1_intro',
        intencion: 'neutral',
      },
    ],
  },
];
